import { create } from 'zustand'
import { toast } from 'react-hot-toast'
import dashboardService from '@services/dashboardService'
import riskExposureService from '@services/riskExposureService'

/**
 * Dashboard Store
 *
 * Zustand store for managing dashboard state across all role-specific dashboards.
 * Handles data fetching, caching, and state management for dashboard widgets.
 */
export const useDashboardStore = create((set, get) => ({
	// ====================================================================
	// STATE
	// ====================================================================

	// Common dashboard state
	stats: null,
	projects: [],
	activity: [],
	deadlines: [],
	teamPerformance: [],
	taskSummary: null,

	// Super Admin specific state
	companyGrowth: null,
	revenueData: null,
	companiesOverview: [],
	packageDistribution: null,
	alerts: [],

	// Role-specific state
	myApprovals: [],
	myTasks: [],
	pendingDecisions: [],

	// Compliance Manager specific state
	complianceOverview: null,
	frameworkProgress: [],
	auditReadiness: null,
	projectsNeedingAttention: [],

	// Project Manager specific state
	pmOverview: null,
	pmProjects: [],
	resourceAllocation: [],

	// Executive specific state
	executiveOverview: null,
	strategicMetrics: null,
	riskSummary: null,

	// Risk Exposure Report state (shared by CM, Executive, Company Admin)
	riskExposureReport: null,
	riskExposureStatus: null,
	riskExposureHistory: [],
	riskExposureLoading: false,
	riskExposureError: null,

	// Employee specific state
	employeeOverview: null,
	assignedTasks: [],
	trainingProgress: null,

	// UI state
	loading: false,
	error: null,
	lastUpdated: null,
	autoRefreshInterval: null,
	autoRefreshEnabled: false,

	// ====================================================================
	// COMPANY DASHBOARD ACTIONS
	// ====================================================================

	/**
	 * Fetch all company dashboard data
	 */
	fetchCompanyDashboard: async () => {
		set({ loading: true, error: null })
		try {
			const [overview, projects, activity, deadlines] = await Promise.all([
				dashboardService.getCompanyOverview(),
				dashboardService.getCompanyProjects(),
				dashboardService.getCompanyActivity(),
				dashboardService.getDeadlines()
			])

			set({
				stats: overview.data?.stats || null,
				taskSummary: overview.data?.task_summary || null,
				projects: projects.data || [],
				activity: activity.data || [],
				deadlines: deadlines.data || [],
				loading: false,
				lastUpdated: new Date()
			})
		} catch (error) {
			console.error('Dashboard fetch error:', error)
			set({ error: error.message, loading: false })
			toast.error('Failed to load dashboard data')
		}
	},

	/**
	 * Fetch team performance data (for admins/managers)
	 */
	fetchTeamPerformance: async () => {
		try {
			const response = await dashboardService.getTeamPerformance()
			set({ teamPerformance: response.data || [] })
		} catch (error) {
			console.error('Team performance fetch error:', error)
		}
	},

	// ====================================================================
	// SUPER ADMIN DASHBOARD ACTIONS
	// ====================================================================

	/**
	 * Fetch all super admin dashboard data
	 */
	fetchSuperAdminDashboard: async () => {
		set({ loading: true, error: null })
		try {
			const [overview, growth, alerts, companies] = await Promise.all([
				dashboardService.getSuperAdminOverview(),
				dashboardService.getCompanyGrowth(),
				dashboardService.getAlerts(),
				dashboardService.getCompaniesOverview()
			])

			set({
				stats: overview.data?.stats || null,
				companyGrowth: growth.data || null,
				alerts: alerts.data || [],
				companiesOverview: companies.data || [],
				loading: false,
				lastUpdated: new Date()
			})
		} catch (error) {
			console.error('Super Admin dashboard fetch error:', error)
			set({ error: error.message, loading: false })
			toast.error('Failed to load dashboard data')
		}
	},

	/**
	 * Fetch package distribution data
	 */
	fetchPackageDistribution: async () => {
		try {
			const response = await dashboardService.getPackageDistribution()
			set({ packageDistribution: response.data || null })
		} catch (error) {
			console.error('Package distribution fetch error:', error)
		}
	},

	// ====================================================================
	// ROLE-SPECIFIC ACTIONS
	// ====================================================================

	/**
	 * Fetch pending approvals for PM/Executive
	 */
	fetchMyApprovals: async () => {
		try {
			const response = await dashboardService.getMyApprovals()
			set({ myApprovals: response.data || [] })
		} catch (error) {
			console.error('My approvals fetch error:', error)
		}
	},

	/**
	 * Fetch my tasks summary
	 */
	fetchMyTasksSummary: async () => {
		try {
			const response = await dashboardService.getMyTasksSummary()
			set({ myTasks: response.data || [] })
		} catch (error) {
			console.error('My tasks fetch error:', error)
		}
	},

	/**
	 * Fetch pending decisions for Executive
	 */
	fetchPendingDecisions: async () => {
		try {
			const response = await dashboardService.getPendingDecisions()
			set({ pendingDecisions: response.data || [] })
		} catch (error) {
			console.error('Pending decisions fetch error:', error)
		}
	},

	// ====================================================================
	// COMPLIANCE MANAGER DASHBOARD ACTIONS
	// ====================================================================

	/**
	 * Fetch compliance manager dashboard data
	 */
	fetchComplianceManagerDashboard: async () => {
		set({ loading: true, error: null })
		try {
			const [overview, frameworks, audit, attention] = await Promise.all([
				dashboardService.getComplianceOverview(),
				dashboardService.getFrameworkProgress(),
				dashboardService.getAuditReadiness(),
				dashboardService.getProjectsNeedingAttention()
			])

			set({
				complianceOverview: overview.data || null,
				frameworkProgress: frameworks.data || [],
				auditReadiness: audit.data || null,
				projectsNeedingAttention: attention.data || [],
				loading: false,
				lastUpdated: new Date()
			})
		} catch (error) {
			console.error('Compliance Manager dashboard fetch error:', error)
			set({ error: error.message, loading: false })
			toast.error('Failed to load dashboard data')
		}
	},

	// ====================================================================
	// PROJECT MANAGER DASHBOARD ACTIONS
	// ====================================================================

	/**
	 * Fetch project manager dashboard data
	 */
	fetchProjectManagerDashboard: async () => {
		set({ loading: true, error: null })
		try {
			const [overview, projects, resources, approvals] = await Promise.all([
				dashboardService.getProjectManagerOverview(),
				dashboardService.getPMProjects(),
				dashboardService.getResourceAllocation(),
				dashboardService.getMyApprovals()
			])

			set({
				pmOverview: overview.data || null,
				pmProjects: projects.data || [],
				resourceAllocation: resources.data || [],
				myApprovals: approvals.data || [],
				loading: false,
				lastUpdated: new Date()
			})
		} catch (error) {
			console.error('Project Manager dashboard fetch error:', error)
			set({ error: error.message, loading: false })
			toast.error('Failed to load dashboard data')
		}
	},

	// ====================================================================
	// EXECUTIVE DASHBOARD ACTIONS
	// ====================================================================

	/**
	 * Fetch executive dashboard data
	 */
	fetchExecutiveDashboard: async () => {
		set({ loading: true, error: null })
		try {
			const [overview, metrics, risk, decisions] = await Promise.all([
				dashboardService.getExecutiveOverview(),
				dashboardService.getStrategicMetrics(),
				dashboardService.getRiskSummary(),
				dashboardService.getPendingDecisions()
			])

			set({
				executiveOverview: overview.data || null,
				strategicMetrics: metrics.data || null,
				riskSummary: risk.data || null,
				pendingDecisions: decisions.data || [],
				loading: false,
				lastUpdated: new Date()
			})
		} catch (error) {
			console.error('Executive dashboard fetch error:', error)
			set({ error: error.message, loading: false })
			toast.error('Failed to load dashboard data')
		}
	},

	// ====================================================================
	// EMPLOYEE DASHBOARD ACTIONS
	// ====================================================================

	/**
	 * Fetch employee dashboard data
	 */
	fetchEmployeeDashboard: async () => {
		set({ loading: true, error: null })
		try {
			const [overview, tasks, training, deadlines] = await Promise.all([
				dashboardService.getEmployeeOverview(),
				dashboardService.getAssignedTasks(),
				dashboardService.getTrainingProgress(),
				dashboardService.getDeadlines()
			])

			set({
				employeeOverview: overview.data || null,
				assignedTasks: tasks.data || [],
				trainingProgress: training.data || null,
				deadlines: deadlines.data || [],
				loading: false,
				lastUpdated: new Date()
			})
		} catch (error) {
			console.error('Employee dashboard fetch error:', error)
			set({ error: error.message, loading: false })
			toast.error('Failed to load dashboard data')
		}
	},

	// ====================================================================
	// RISK EXPOSURE REPORT ACTIONS
	// ====================================================================

	/**
	 * Fetch latest risk exposure report
	 */
	fetchRiskExposureReport: async () => {
		set({ riskExposureLoading: true, riskExposureError: null })
		try {
			const response = await riskExposureService.getLatest()
			set({
				riskExposureReport: response.has_report ? response.data : null,
				riskExposureLoading: false
			})
			return response
		} catch (error) {
			console.error('Risk exposure report fetch error:', error)
			set({
				riskExposureError: error.message,
				riskExposureLoading: false
			})
			return null
		}
	},

	/**
	 * Check risk exposure report generation status
	 */
	fetchRiskExposureStatus: async () => {
		try {
			const response = await riskExposureService.getStatus()
			set({ riskExposureStatus: response.data })
			return response.data
		} catch (error) {
			console.error('Risk exposure status fetch error:', error)
			return null
		}
	},

	/**
	 * Fetch risk exposure report history
	 */
	fetchRiskExposureHistory: async (limit = 5) => {
		try {
			const response = await riskExposureService.getHistory(limit)
			set({ riskExposureHistory: response.data || [] })
			return response.data
		} catch (error) {
			console.error('Risk exposure history fetch error:', error)
			return []
		}
	},

	/**
	 * Generate new risk exposure report
	 */
	generateRiskExposureReport: async () => {
		set({ riskExposureLoading: true, riskExposureError: null })
		try {
			const response = await riskExposureService.generate()

			if (response.status === 'already_processing') {
				set({
					riskExposureStatus: {
						is_generating: true,
						report_id: response.report_id,
						status: 'processing',
						started_at: response.started_at
					},
					riskExposureLoading: false
				})
				toast.success('Report generation is already in progress')
				return response
			}

			if (response.status === 'completed') {
				// Report completed immediately (no risk checklists case)
				await get().fetchRiskExposureReport()
				toast.success('Report generated successfully')
				return response
			}

			// Report is processing
			set({
				riskExposureStatus: {
					is_generating: true,
					report_id: response.report_id,
					status: 'processing',
					started_at: response.started_at
				},
				riskExposureLoading: false
			})
			toast.success('Report generation started. You will be notified when complete.')
			return response
		} catch (error) {
			console.error('Risk exposure report generation error:', error)
			set({
				riskExposureError: error.message || 'Failed to generate report',
				riskExposureLoading: false
			})
			toast.error(error.message || 'Failed to generate report')
			return null
		}
	},

	/**
	 * Load a specific historical report
	 */
	loadRiskExposureReportById: async (reportId) => {
		set({ riskExposureLoading: true, riskExposureError: null })
		try {
			const response = await riskExposureService.getById(reportId)
			set({
				riskExposureReport: response.data,
				riskExposureLoading: false
			})
			return response.data
		} catch (error) {
			console.error('Risk exposure report load error:', error)
			set({
				riskExposureError: error.message,
				riskExposureLoading: false
			})
			return null
		}
	},

	/**
	 * Clear risk exposure error
	 */
	clearRiskExposureError: () => {
		set({ riskExposureError: null })
	},

	// ====================================================================
	// UTILITY ACTIONS
	// ====================================================================

	/**
	 * Refresh dashboard data based on role
	 * @param {string} role - User role slug or constant
	 */
	refreshDashboard: async (role) => {
		const roleKey = role?.toLowerCase?.() || role

		switch (roleKey) {
			case 'super_admin':
			case 'super-admin':
				await get().fetchSuperAdminDashboard()
				break
			case 'company_admin':
			case 'company-admin':
				await get().fetchCompanyDashboard()
				break
			case 'compliance_manager':
			case 'compliance-manager':
				await get().fetchComplianceManagerDashboard()
				break
			case 'project_manager':
			case 'project-manager':
				await get().fetchProjectManagerDashboard()
				break
			case 'executive':
				await get().fetchExecutiveDashboard()
				break
			case 'employee':
				await get().fetchEmployeeDashboard()
				break
			default:
				// Default to company dashboard for unknown roles
				await get().fetchCompanyDashboard()
		}
	},

	/**
	 * Set loading state
	 */
	setLoading: (loading) => {
		set({ loading })
	},

	/**
	 * Clear error
	 */
	clearError: () => {
		set({ error: null })
	},

	/**
	 * Reset store to initial state
	 */
	reset: () => {
		const { autoRefreshInterval } = get()
		if (autoRefreshInterval) {
			clearInterval(autoRefreshInterval)
		}

		set({
			// Common state
			stats: null,
			projects: [],
			activity: [],
			deadlines: [],
			teamPerformance: [],
			taskSummary: null,

			// Super Admin state
			companyGrowth: null,
			revenueData: null,
			companiesOverview: [],
			packageDistribution: null,
			alerts: [],

			// Role-specific state
			myApprovals: [],
			myTasks: [],
			pendingDecisions: [],

			// Compliance Manager state
			complianceOverview: null,
			frameworkProgress: [],
			auditReadiness: null,
			projectsNeedingAttention: [],

			// Project Manager state
			pmOverview: null,
			pmProjects: [],
			resourceAllocation: [],

			// Executive state
			executiveOverview: null,
			strategicMetrics: null,
			riskSummary: null,

			// Risk Exposure Report state
			riskExposureReport: null,
			riskExposureStatus: null,
			riskExposureHistory: [],
			riskExposureLoading: false,
			riskExposureError: null,

			// Employee state
			employeeOverview: null,
			assignedTasks: [],
			trainingProgress: null,

			// UI state
			loading: false,
			error: null,
			lastUpdated: null,
			autoRefreshInterval: null,
			autoRefreshEnabled: false
		})
	},

	// ====================================================================
	// AUTO-REFRESH ACTIONS
	// ====================================================================

	/**
	 * Start auto-refresh for dashboard data
	 * @param {string} role - User role for dashboard type
	 * @param {number} intervalMs - Refresh interval in milliseconds (default: 5 minutes)
	 * @returns {Function} Cleanup function to stop auto-refresh
	 */
	startAutoRefresh: (role, intervalMs = 300000) => {
		const { autoRefreshInterval, refreshDashboard } = get()

		// Clear existing interval
		if (autoRefreshInterval) {
			clearInterval(autoRefreshInterval)
		}

		// Set up auto-refresh
		const interval = setInterval(() => {
			refreshDashboard(role)
		}, intervalMs)

		set({
			autoRefreshInterval: interval,
			autoRefreshEnabled: true
		})

		// Return cleanup function
		return () => {
			clearInterval(interval)
			set({
				autoRefreshInterval: null,
				autoRefreshEnabled: false
			})
		}
	},

	/**
	 * Stop auto-refresh
	 */
	stopAutoRefresh: () => {
		const { autoRefreshInterval } = get()
		if (autoRefreshInterval) {
			clearInterval(autoRefreshInterval)
			set({
				autoRefreshInterval: null,
				autoRefreshEnabled: false
			})
		}
	},

	/**
	 * Toggle auto-refresh on/off
	 * @param {string} role - User role for dashboard type
	 * @param {number} intervalMs - Refresh interval in milliseconds
	 */
	toggleAutoRefresh: (role, intervalMs = 300000) => {
		const { autoRefreshEnabled, startAutoRefresh, stopAutoRefresh } = get()
		if (autoRefreshEnabled) {
			stopAutoRefresh()
		} else {
			startAutoRefresh(role, intervalMs)
		}
	},

	/**
	 * Get time since last update in human-readable format
	 * @returns {string} Time since last update
	 */
	getTimeSinceUpdate: () => {
		const { lastUpdated } = get()
		if (!lastUpdated) return 'Never'

		const seconds = Math.floor((new Date() - lastUpdated) / 1000)

		if (seconds < 60) return 'Just now'
		if (seconds < 120) return '1 minute ago'
		if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`
		if (seconds < 7200) return '1 hour ago'
		return `${Math.floor(seconds / 3600)} hours ago`
	}
}))

export default useDashboardStore
